import LogoComponent from "@/components/logo/LogoComponent";
import React from "react";

const Section: React.FC<{
    number: string;
    title: string;
    children: React.ReactNode;
}> = ({ number, title, children }) => (
    <section className="mb-8">
        <h2 className="mb-3 text-xl font-semibold text-amber-400">
            {number}. {title}
        </h2>
        {children}
    </section>
);

const PrivacyPolicyPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-slate-900 px-4 py-12 text-slate-200">
            <div className="mx-auto max-w-3xl">
                {/* Header */}
                <div className="mb-10 flex flex-col items-center">
                    <LogoComponent className="mb-6 h-16 w-auto" />
                    <h1 className="mb-1 text-center text-3xl font-bold text-amber-500">
                        Privacy Policy
                    </h1>
                    <p className="text-center text-sm text-slate-400">
                        VANDERHEIM Extension &amp; Web App
                    </p>
                    <p className="mt-1 text-center text-xs text-slate-500">
                        Effective Date: 23rd March 2026
                    </p>
                </div>

                <p className="mb-8 leading-relaxed text-slate-300">
                    This Privacy Policy explains how we collect, use, store, and
                    protect your information when you use the VANDERHEIM Twitch
                    extension, associated web application, and related services
                    ("Services").
                </p>
                <p className="mb-10 leading-relaxed text-slate-300">
                    By using the Services, you agree to the collection and use
                    of information in accordance with this policy.
                </p>

                <div className="mb-10 border-t border-slate-700" />

                <Section number="1" title="Information We Collect">
                    <p className="mb-4 leading-relaxed text-slate-300">
                        When you connect your Twitch account to the Services, we
                        may collect and process the following information:
                    </p>
                    <h3 className="mb-2 font-medium text-amber-300">
                        Account Information
                    </h3>
                    <ul className="mb-4 list-inside list-disc space-y-1 pl-2 text-slate-300">
                        <li>Twitch User ID</li>
                        <li>Twitch username / display name</li>
                        <li>
                            Profile-related metadata provided via Twitch APIs
                        </li>
                    </ul>
                    <h3 className="mb-2 font-medium text-amber-300">
                        Linked Platform Data
                    </h3>
                    <p className="mb-2 leading-relaxed text-slate-300">
                        If you connect additional services (e.g., Discord), we
                        may collect:
                    </p>
                    <ul className="mb-4 list-inside list-disc space-y-1 pl-2 text-slate-300">
                        <li>Discord User ID</li>
                        <li>Discord username</li>
                        <li>
                            Relevant server membership or role data (where
                            applicable)
                        </li>
                    </ul>
                    <h3 className="mb-2 font-medium text-amber-300">
                        Usage Data
                    </h3>
                    <ul className="list-inside list-disc space-y-1 pl-2 text-slate-300">
                        <li>Interaction data within the VANDERHEIM platform</li>
                        <li>
                            Activity related to features (e.g., tags, rewards,
                            progression systems)
                        </li>
                    </ul>
                </Section>

                <Section number="2" title="How We Use Your Information">
                    <p className="mb-3 leading-relaxed text-slate-300">
                        We use your information strictly for the operation and
                        improvement of the Services, including:
                    </p>
                    <ul className="mb-4 list-inside list-disc space-y-1 pl-2 text-slate-300">
                        <li>Authenticating your account via Twitch</li>
                        <li>
                            Linking your Twitch and Discord identities (if
                            enabled)
                        </li>
                        <li>
                            Powering platform features (e.g., rewards,
                            progression, community systems)
                        </li>
                        <li>
                            Maintaining internal records and system integrity
                        </li>
                        <li>Improving functionality and user experience</li>
                    </ul>
                    <p className="leading-relaxed text-slate-300">
                        We do not use your data for advertising or profiling
                        outside the scope of the platform.
                    </p>
                </Section>

                <Section number="3" title="Data Identification & Anonymisation">
                    <ul className="list-inside list-disc space-y-2 pl-2 text-slate-300">
                        <li>
                            All users within the system are primarily identified
                            by unique internal ID numbers (e.g., serialised user
                            IDs).
                        </li>
                        <li>
                            Public-facing features use these identifiers where
                            possible to reduce exposure of personal data.
                        </li>
                        <li>
                            We do not attempt to enrich or cross-reference your
                            data with external datasets.
                        </li>
                    </ul>
                </Section>

                <Section number="4" title="Data Sharing">
                    <p className="mb-3 leading-relaxed text-slate-300">
                        We do not sell, rent, or share your personal data with
                        third parties. Data is only shared in the following
                        limited contexts:
                    </p>
                    <ul className="mb-4 list-inside list-disc space-y-1 pl-2 text-slate-300">
                        <li>
                            With Twitch, as required for authentication and API
                            functionality
                        </li>
                        <li>
                            With Discord, only where you explicitly link your
                            account and enable integration
                        </li>
                    </ul>
                    <p className="leading-relaxed text-slate-300">
                        No additional third-party data sharing occurs.
                    </p>
                </Section>

                <Section number="5" title="Data Storage & Security">
                    <ul className="list-inside list-disc space-y-2 pl-2 text-slate-300">
                        <li>
                            All data is stored on secure servers located in the
                            United Kingdom (UK)
                        </li>
                        <li>
                            We implement reasonable technical and organisational
                            measures to protect your data
                        </li>
                        <li>
                            Access to data is restricted to systems and
                            personnel required to operate the Services
                        </li>
                        <li>
                            While we take security seriously, no system can be
                            guaranteed 100% secure.
                        </li>
                    </ul>
                </Section>

                <Section number="6" title="Data Retention">
                    <p className="mb-3 leading-relaxed text-slate-300">
                        We retain your data only for as long as necessary to:
                    </p>
                    <ul className="mb-4 list-inside list-disc space-y-1 pl-2 text-slate-300">
                        <li>Provide the Services</li>
                        <li>Maintain system integrity and records</li>
                        <li>Comply with legal obligations (if applicable)</li>
                    </ul>
                    <p className="leading-relaxed text-slate-300">
                        You may request deletion of your data at any time (see
                        Section 8).
                    </p>
                </Section>

                <Section number="7" title="Your Rights">
                    <p className="mb-3 leading-relaxed text-slate-300">
                        Depending on your location, you may have rights
                        including:
                    </p>
                    <ul className="mb-4 list-inside list-disc space-y-1 pl-2 text-slate-300">
                        <li>Access to the data we hold about you</li>
                        <li>Correction of inaccurate data</li>
                        <li>Deletion of your data</li>
                        <li>Restriction of processing</li>
                    </ul>
                    <p className="leading-relaxed text-slate-300">
                        We will make reasonable efforts to comply with all valid
                        requests.
                    </p>
                </Section>

                <Section
                    number="8"
                    title="Account Disconnection & Data Deletion"
                >
                    <p className="mb-3 leading-relaxed text-slate-300">
                        You may:
                    </p>
                    <ul className="mb-4 list-inside list-disc space-y-1 pl-2 text-slate-300">
                        <li>Disconnect your Twitch account at any time</li>
                        <li>Request full deletion of your data</li>
                    </ul>
                    <p className="mb-2 leading-relaxed text-slate-300">
                        To request deletion, contact:{" "}
                        <a
                            href="mailto:privacy@vanderheim.com"
                            className="text-amber-400 underline hover:text-amber-300"
                        >
                            privacy@vanderheim.com
                        </a>
                    </p>
                    <p className="leading-relaxed text-slate-300">
                        Upon request, your data will be deleted within a
                        reasonable timeframe unless retention is required for
                        legitimate operational or legal reasons.
                    </p>
                </Section>

                <Section number="9" title="Children's Privacy">
                    <p className="leading-relaxed text-slate-300">
                        The Services are not intended for individuals under the
                        age of 13 (or the applicable minimum age in your
                        region). We do not knowingly collect data from children.
                    </p>
                </Section>

                <Section number="10" title="Changes to This Policy">
                    <p className="leading-relaxed text-slate-300">
                        We may update this Privacy Policy from time to time.
                        Updates will be reflected by a revised "Effective Date."
                        Continued use of the Services after changes constitutes
                        acceptance of the updated policy.
                    </p>
                </Section>

                <Section number="11" title="Contact">
                    <p className="leading-relaxed text-slate-300">
                        For any questions or concerns regarding this Privacy
                        Policy, contact:{" "}
                        <a
                            href="mailto:privacy@vanderheim.com"
                            className="text-amber-400 underline hover:text-amber-300"
                        >
                            privacy@vanderheim.com
                        </a>
                    </p>
                </Section>

                <div className="mt-10 border-t border-slate-700 pt-6 text-center text-xs text-slate-500">
                    &copy; VANDERHEIM – All rights reserved.
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
